import axios from 'axios';
import API from '../config/API';

/**
 * Plan & usage (pricing, Oct 2026). /api/v2/billing/*, accessToken auth.
 * Money is integer pence throughout.
 *
 * Local development can run against a fixture instead of the API: start the
 * app with REACT_APP_PRICING_MOCK=true (or set localStorage
 * `beu.pricingMock` to a scenario name, see pricingMock.dev.js). The fixture
 * is required inside a NODE_ENV check, so a production build (every deployed
 * build, staging included) never contains it.
 */
let mockApi = null;
if (process.env.NODE_ENV !== 'production') {
    let wanted = process.env.REACT_APP_PRICING_MOCK === 'true';
    try { wanted = wanted || !!localStorage.getItem('beu.pricingMock'); } catch (e) { /* no storage */ }
    if (wanted && process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line global-require
        mockApi = require('../component/pricing/pricingMock.dev').default;
    }
}

const headers = () => ({ 'Content-Type': 'application/json', accessToken: localStorage.getItem('accessToken') || '' });
const salonIdNow = () => { try { return String(localStorage.getItem('salonId') || ''); } catch (e) { return ''; } };

/** An Error carrying the API's code and data, so callers can branch on it. */
function apiError(body, fallback) {
    const err = (body && body.error) || {};
    const data = (body && (err.data || body.data || body.responseData)) || {};
    const e = new Error(err.responseMessage || (body && body.message) || fallback || 'That didn’t work. Try again.');
    e.code = err.code || data.code || (body && body.code) || null;
    e.data = { ...data, ...(err.shortfallPence != null ? { shortfallPence: err.shortfallPence } : {}) };
    return e;
}

function unwrap(res) {
    const body = (res && res.data) || {};
    if (body.statusCode === 0 || body.error) throw apiError(body);
    return body.responseData !== undefined ? body.responseData : (body.data !== undefined ? body.data : body);
}
const rethrow = (e) => {
    if (e && e.code && e.data) throw e; // already ours
    const body = e && e.response && e.response.data;
    const status = e && e.response && e.response.status;
    if (body && (body.error || body.message)) { const err = apiError(body); err.status = status; throw err; }
    if (status) e.status = status;
    throw e;
};

/** A POST/PUT returns { usage }; an older shape may return the usage itself. */
const usageOf = (d) => (d && d.usage) || (d && d.plan ? d : null);

const real = {
    // fresh: ask the server to skip its plan cache (before the upgrade modal,
    // so the charge line can't be stale). Harmless if ignored.
    usage: (salonId, opts = {}) => axios.get(`${API.BILLING}/usage`, { headers: headers(), params: { salonId: salonId || salonIdNow(), ...(opts.fresh ? { fresh: 1 } : {}) } }).then(unwrap, rethrow),
    baileyUpgrade: (action, salonId) => axios.post(`${API.BILLING}/bailey-upgrade`, { salonId: salonId || salonIdNow(), action }, { headers: headers() }).then(unwrap, rethrow).then(usageOf),
    buyPack: (salonId, count = 1) => axios.post(`${API.BILLING}/receptionist-pack`, { salonId: salonId || salonIdNow(), count }, { headers: headers() }).then(unwrap, rethrow).then(usageOf),
    // Save a card for off-session charges (settling messages, top-ups, the
    // upgrade) on the caller's own customer: SetupIntent → Stripe → confirm.
    cardSetupIntent: (salonId) => axios.post(`${API.BILLING}/card/setup-intent`, { salonId: salonId || salonIdNow() }, { headers: headers() }).then(unwrap, rethrow),
    cardConfirm: (setupIntentId, salonId) => axios.post(`${API.BILLING}/card/confirm`, { salonId: salonId || salonIdNow(), setupIntentId }, { headers: headers() }).then(unwrap, rethrow).then(usageOf),
    // Spend protection: the monthly limit on automatic charges (£10–£500),
    // and resuming automatic top-ups after an unusual-activity pause.
    setAutoSpendCap: (capPence, salonId) => axios.put(`${API.BILLING}/auto-spend-cap`, { salonId: salonId || salonIdNow(), capPence }, { headers: headers() }).then(unwrap, rethrow).then(usageOf),
    autoResume: (salonId) => axios.post(`${API.BILLING}/auto-resume`, { salonId: salonId || salonIdNow() }, { headers: headers() }).then(unwrap, rethrow).then(usageOf),
    // The AI receptionist add-on (2026-10-07): on/off, the one free trial per
    // business, and the owner's monthly limit on pay as you go.
    receptionist: (action, salonId) => axios.post(`${API.BILLING}/receptionist`, { salonId: salonId || salonIdNow(), action }, { headers: headers() }).then(unwrap, rethrow).then(usageOf),
    receptionistTrial: (salonId) => axios.post(`${API.BILLING}/receptionist-trial`, { salonId: salonId || salonIdNow() }, { headers: headers() }).then(unwrap, rethrow).then(usageOf),
    setReceptionistCap: (capPence, salonId) => axios.put(`${API.BILLING}/receptionist-cap`, { salonId: salonId || salonIdNow(), capPence }, { headers: headers() }).then(unwrap, rethrow).then(usageOf),
    // Bailey for employed team members: { plus, staff: [{ userId, name, enabled, active }] }
    baileyStaff: (salonId) => axios.get(`${API.BILLING}/bailey-staff`, { headers: headers(), params: { salonId: salonId || salonIdNow() } }).then(unwrap, rethrow),
    setBaileyStaff: (userId, enabled, salonId) => axios.put(`${API.BILLING}/bailey-staff`, { salonId: salonId || salonIdNow(), userId, enabled: !!enabled }, { headers: headers() }).then(unwrap, rethrow),
    setAutoPacks: (enabled, salonId) => axios.put(`${API.BILLING}/receptionist-auto-packs`, { salonId: salonId || salonIdNow(), enabled: !!enabled }, { headers: headers() }).then(unwrap, rethrow).then(usageOf),
};

export const pricingService = mockApi || real;
export const pricingMockActive = !!mockApi;
export const currentSalonId = salonIdNow;
export default pricingService;
